import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatCurrency, formatDateLong } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ClipboardList, Plus, Search, ArrowLeft, Upload, FileText, Check, X, ArrowRightLeft, ChevronLeft, ChevronRight, Loader2, AlertTriangle, Trash2, Settings2, Eye } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  "Pending Review": "bg-yellow-100 text-yellow-800",
  "Matched": "bg-blue-100 text-blue-800",
  "Approved": "bg-green-100 text-green-800",
  "Rejected": "bg-red-100 text-red-800",
  "Converted": "bg-purple-100 text-purple-800",
};

// ======================== LIST ========================

function CPOList() {
  const [cpos, setCpos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchCPOs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/customer-pos", { params: { page, limit: 15, search, status: statusFilter } });
      setCpos(r.data.customer_pos || []);
      setTotal(r.data.total || 0);
    } catch { toast.error("Failed to load CPOs"); }
    finally { setLoading(false); }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchCPOs(); }, [fetchCPOs]);

  const counts = { pending: 0, matched: 0, approved: 0 };
  cpos.forEach(c => { if (c.status === "Pending Review") counts.pending++; if (c.status === "Matched") counts.matched++; if (c.status === "Approved") counts.approved++; });

  return (
    <div className="space-y-5" data-testid="cpo-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Customer Purchase Orders</h1>
          <div className="flex gap-3 mt-1 text-xs">
            <span className="text-yellow-600 font-medium">{counts.pending} Pending</span>
            <span className="text-blue-600 font-medium">{counts.matched} Matched</span>
            <span className="text-green-600 font-medium">{counts.approved} Approved</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} className="rounded-sm" data-testid="cpo-settings-btn">
              <Settings2 className="w-4 h-4 mr-1" />Settings
            </Button>
          )}
          <Button variant="outline" onClick={() => setUploadOpen(true)} className="rounded-sm" data-testid="cpo-upload-btn">
            <Upload className="w-4 h-4 mr-1" />Upload PO
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm" data-testid="cpo-create-btn">
            <Plus className="w-4 h-4 mr-1" />New CPO
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search CPO#, customer, PO#..." className="pl-9 rounded-sm border-gray-300" data-testid="cpo-search" />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-40 rounded-sm border-gray-300" data-testid="cpo-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {["Pending Review", "Matched", "Approved", "Rejected", "Converted"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#E63946] animate-spin" /></div>
      ) : cpos.length === 0 ? (
        <div className="text-center py-16"><ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-500 text-sm">No customer purchase orders found</p></div>
      ) : (
        <div className="bg-white rounded-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              <th className="text-left px-4 py-3">CPO #</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Customer PO#</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-left px-4 py-3">Matched Quote</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Date</th>
            </tr></thead>
            <tbody>
              {cpos.map(cpo => (
                <tr key={cpo.cpo_id} onClick={() => navigate(`/customer-purchase-orders/${cpo.cpo_id}`)}
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors" data-testid={`cpo-row-${cpo.cpo_id}`}>
                  <td className="px-4 py-3 font-medium text-[#E63946]">{cpo.cpo_number}</td>
                  <td className="px-4 py-3 text-[#1F2937]">{cpo.customer_name}</td>
                  <td className="px-4 py-3 text-gray-600">{cpo.customer_po_number || "—"}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="text-xs capitalize">{cpo.source}</Badge></td>
                  <td className="px-4 py-3 text-blue-600">{cpo.matched_quote_number || "—"}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatCurrency(cpo.total_amount)}</td>
                  <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[cpo.status] || "bg-gray-100 text-gray-800"}`}>{cpo.status}</span></td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDateLong(cpo.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > 15 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, total)} of {total}</p>
          <div className="flex gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page * 15 >= total} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      <CPOCreateDialog open={createOpen} onOpenChange={setCreateOpen} onSuccess={() => { setCreateOpen(false); fetchCPOs(); }} />
      <CPOUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onSuccess={() => { setUploadOpen(false); fetchCPOs(); }} />
      {settingsOpen && <CPOSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />}
    </div>
  );
}

// ======================== CREATE ========================

function CPOCreateDialog({ open, onOpenChange, onSuccess }) {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [customerPoNumber, setCustomerPoNumber] = useState("");
  const [poDate, setPoDate] = useState(new Date().toISOString().split("T")[0]);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [quoteRef, setQuoteRef] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/customers", { params: { limit: 200, status: "Active" } }).then(r => setCustomers(r.data.customers || [])).catch(() => {});
      api.get("/products", { params: { limit: 200, status: "Active" } }).then(r => setProducts(r.data.products || [])).catch(() => {});
      setCustomerId(""); setCustomerPoNumber(""); setPoDate(new Date().toISOString().split("T")[0]); setDeliveryDate(""); setQuoteRef(""); setNotes(""); setItems([]);
    }
  }, [open]);

  const addProduct = (p) => {
    setItems([...items, { id: Date.now(), description: p.product_name, quantity: 1, unit_price: p.selling_price || 0 }]);
    setProductSearch("");
  };
  const addCustom = () => setItems([...items, { id: Date.now(), description: "", quantity: 1, unit_price: 0 }]);
  const updateItem = (idx, f, v) => { const n = [...items]; n[idx] = { ...n[idx], [f]: v }; setItems(n); };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const total = items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);
  const filtered = productSearch ? products.filter(p => p.product_name?.toLowerCase().includes(productSearch.toLowerCase()) || p.sku?.toLowerCase().includes(productSearch.toLowerCase())).slice(0, 6) : [];

  const handleSave = async () => {
    if (!customerId) { toast.error("Select a customer"); return; }
    if (items.length === 0) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try {
      await api.post("/customer-pos", {
        customer_id: customerId, customer_po_number: customerPoNumber, po_date: poDate,
        delivery_date: deliveryDate, quote_reference: quoteRef, notes, line_items: items,
      });
      toast.success("Customer PO created"); onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create CPO"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cpo-create-dialog">
        <DialogHeader><DialogTitle>Create Customer Purchase Order</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer <span className="text-[#E63946]">*</span></label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="rounded-sm" data-testid="cpo-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer PO#</label>
              <Input value={customerPoNumber} onChange={e => setCustomerPoNumber(e.target.value)} placeholder="PO-12345" className="rounded-sm" data-testid="cpo-po-number" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quote Reference</label>
              <Input value={quoteRef} onChange={e => setQuoteRef(e.target.value)} placeholder="QT-0001" className="rounded-sm" data-testid="cpo-quote-ref" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">PO Date</label><Input type="date" value={poDate} onChange={e => setPoDate(e.target.value)} className="rounded-sm" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Delivery Date</label><Input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} className="rounded-sm" /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Line Items</label>
              <div className="flex gap-2">
                <div className="relative">
                  <Input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Search products..." className="rounded-sm h-8 w-44 text-xs" />
                  {filtered.length > 0 && (
                    <div className="absolute z-50 top-full left-0 w-64 bg-white border rounded-sm shadow-lg mt-1 max-h-40 overflow-y-auto">
                      {filtered.map(p => <button key={p.product_id} onClick={() => addProduct(p)} className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 border-b last:border-0">{p.product_name} — {formatCurrency(p.selling_price)}</button>)}
                    </div>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={addCustom} className="rounded-sm text-xs h-8"><Plus className="w-3 h-3 mr-1" />Custom</Button>
              </div>
            </div>
            {items.length > 0 && (
              <div className="border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 border-b text-xs text-gray-500"><th className="text-left px-3 py-2">Description</th><th className="text-right px-3 py-2 w-20">Qty</th><th className="text-right px-3 py-2 w-28">Price</th><th className="text-right px-3 py-2 w-24">Total</th><th className="w-8"></th></tr></thead>
                  <tbody>{items.map((it, idx) => (
                    <tr key={it.id} className="border-b border-gray-100">
                      <td className="px-2 py-1"><Input value={it.description} onChange={e => updateItem(idx, "description", e.target.value)} placeholder="Description" className="h-8 text-sm rounded-sm" /></td>
                      <td className="px-2 py-1"><Input type="number" min="1" value={it.quantity} onChange={e => updateItem(idx, "quantity", e.target.value)} className="h-8 text-sm text-right rounded-sm" /></td>
                      <td className="px-2 py-1"><Input type="number" step="0.01" value={it.unit_price} onChange={e => updateItem(idx, "unit_price", e.target.value)} className="h-8 text-sm text-right rounded-sm" /></td>
                      <td className="px-3 py-1 text-right font-medium">{formatCurrency((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0))}</td>
                      <td className="px-1 py-1"><Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-[#E63946]" onClick={() => removeItem(idx)}><X className="w-3.5 h-3.5" /></Button></td>
                    </tr>
                  ))}</tbody>
                </table>
                <div className="bg-gray-50 px-4 py-2 text-right font-bold border-t">Total: {formatCurrency(total)}</div>
              </div>
            )}
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-16 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm" data-testid="cpo-save-btn">{saving ? "Saving..." : "Create CPO"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ======================== UPLOAD ========================

function CPOUploadDialog({ open, onOpenChange, onSuccess }) {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      api.get("/customers", { params: { limit: 200, status: "Active" } }).then(r => setCustomers(r.data.customers || [])).catch(() => {});
      setCustomerId(""); setFile(null); setResult(null);
    }
  }, [open]);

  const handleUpload = async () => {
    if (!customerId) { toast.error("Select a customer"); return; }
    if (!file) { toast.error("Select a file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("customer_id", customerId);
      const r = await api.post("/customer-pos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`PO processed: ${r.data.status}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="cpo-upload-dialog">
        <DialogHeader><DialogTitle>Upload Customer PO</DialogTitle></DialogHeader>
        {!result ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer <span className="text-[#E63946]">*</span></label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="rounded-sm" data-testid="cpo-upload-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PO File (PDF/Image) <span className="text-[#E63946]">*</span></label>
              <div className="border-2 border-dashed border-gray-300 rounded-sm p-6 text-center">
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" id="cpo-file-input" data-testid="cpo-file-input" />
                <label htmlFor="cpo-file-input" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-500">{file ? file.name : "Click to select file"}</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, JPG, or PNG up to 10MB</p>
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={handleUpload} disabled={uploading} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm" data-testid="cpo-upload-submit">
                {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Processing...</> : "Upload & Process"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4" data-testid="cpo-upload-result">
            <div className="bg-green-50 border border-green-200 rounded-sm p-4">
              <p className="text-sm font-semibold text-green-800 mb-2">PO Processed Successfully</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <p><span className="text-gray-500">CPO#:</span> <span className="font-medium">{result.cpo_number}</span></p>
                <p><span className="text-gray-500">Status:</span> <span className={`font-medium ${STATUS_COLORS[result.status]?.includes("text-") ? "" : ""}`}>{result.status}</span></p>
                {result.customer_po_number && <p><span className="text-gray-500">Customer PO#:</span> <span className="font-medium">{result.customer_po_number}</span></p>}
                <p><span className="text-gray-500">Total:</span> <span className="font-medium">{formatCurrency(result.total_amount)}</span></p>
                {result.matched_quote_number && <p className="col-span-2"><span className="text-gray-500">Matched Quote:</span> <span className="font-medium text-blue-600">{result.matched_quote_number}</span></p>}
              </div>
            </div>
            {result.ocr_data?.success === false && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                <p className="text-xs text-amber-700"><AlertTriangle className="w-3 h-3 inline mr-1" />OCR processing had issues. Please review manually.</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { onOpenChange(false); onSuccess(); }} className="rounded-sm">Close</Button>
              <Button onClick={() => { onOpenChange(false); navigate(`/customer-purchase-orders/${result.cpo_id}`); }} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm" data-testid="cpo-view-result">
                <Eye className="w-4 h-4 mr-1" />View CPO
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ======================== DETAIL ========================

function CPODetail({ cpoId }) {
  const [cpo, setCpo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matchOpen, setMatchOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [converting, setConverting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canApprove = ["admin", "sales_manager"].includes(user?.role);

  const fetch = useCallback(() => {
    api.get(`/customer-pos/${cpoId}`).then(r => setCpo(r.data)).catch(() => toast.error("CPO not found")).finally(() => setLoading(false));
  }, [cpoId]);
  useEffect(() => { fetch(); }, [fetch]);

  const approve = async () => {
    try { await api.post(`/customer-pos/${cpoId}/approve`); toast.success("CPO approved"); fetch(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reject = async () => {
    try { await api.post(`/customer-pos/${cpoId}/reject`, { reason: rejectReason }); toast.success("CPO rejected"); setRejectOpen(false); fetch(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const convert = async () => {
    setConverting(true);
    try {
      const r = await api.post(`/customer-pos/${cpoId}/convert-to-invoice`);
      toast.success(`Invoice ${r.data.invoice?.invoice_number} created`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setConverting(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#E63946] animate-spin" /></div>;
  if (!cpo) return <div className="text-center py-16"><p className="text-gray-500">CPO not found</p></div>;

  const matchedQuote = cpo.quote_matches?.find(q => q.quote_id === cpo.matched_quote_id);

  return (
    <div className="space-y-5" data-testid="cpo-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/customer-purchase-orders")} className="rounded-sm" data-testid="cpo-back"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#1F2937]">{cpo.cpo_number}</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[cpo.status] || "bg-gray-100"}`}>{cpo.status}</span>
            <Badge variant="outline" className="text-xs capitalize">{cpo.source}</Badge>
            <span className="text-sm text-gray-500">{cpo.customer_name}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {cpo.status === "Pending Review" && canApprove && (
            <>
              <Button variant="outline" onClick={() => setMatchOpen(true)} className="rounded-sm" data-testid="cpo-match-btn"><ArrowRightLeft className="w-4 h-4 mr-1" />Match Quote</Button>
              <Button onClick={approve} className="bg-green-600 hover:bg-green-700 text-white rounded-sm" data-testid="cpo-approve-btn"><Check className="w-4 h-4 mr-1" />Approve</Button>
              <Button variant="outline" onClick={() => setRejectOpen(true)} className="text-red-600 hover:text-red-700 rounded-sm" data-testid="cpo-reject-btn"><X className="w-4 h-4 mr-1" />Reject</Button>
            </>
          )}
          {cpo.status === "Matched" && canApprove && (
            <>
              <Button onClick={approve} className="bg-green-600 hover:bg-green-700 text-white rounded-sm" data-testid="cpo-approve-btn"><Check className="w-4 h-4 mr-1" />Approve</Button>
              <Button variant="outline" onClick={() => setRejectOpen(true)} className="text-red-600 hover:text-red-700 rounded-sm"><X className="w-4 h-4 mr-1" />Reject</Button>
            </>
          )}
          {cpo.status === "Approved" && !cpo.converted_to_invoice_id && (
            <Button onClick={convert} disabled={converting} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm" data-testid="cpo-convert-btn">
              {converting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}Convert to Invoice
            </Button>
          )}
          {cpo.converted_to_invoice_id && (
            <Button variant="outline" onClick={() => navigate(`/invoices/${cpo.converted_to_invoice_id}`)} className="rounded-sm text-purple-600" data-testid="cpo-view-invoice">
              <FileText className="w-4 h-4 mr-1" />View Invoice
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: CPO Details */}
        <div className="lg:col-span-2 space-y-5">
          <Card className="rounded-sm border-gray-200">
            <CardContent className="p-5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Order Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><p className="text-xs text-gray-500">Customer PO#</p><p className="font-medium">{cpo.customer_po_number || "—"}</p></div>
                <div><p className="text-xs text-gray-500">PO Date</p><p className="font-medium">{cpo.po_date || "—"}</p></div>
                <div><p className="text-xs text-gray-500">Delivery Date</p><p className="font-medium">{cpo.delivery_date || "—"}</p></div>
                <div><p className="text-xs text-gray-500">Total Amount</p><p className="font-bold text-lg text-[#E63946]">{formatCurrency(cpo.total_amount)}</p></div>
              </div>
              {cpo.notes && <div className="mt-4 pt-4 border-t"><p className="text-xs text-gray-500 mb-1">Notes</p><p className="text-sm text-gray-700">{cpo.notes}</p></div>}
            </CardContent>
          </Card>

          {/* Line Items */}
          {cpo.line_items?.length > 0 && (
            <Card className="rounded-sm border-gray-200">
              <CardContent className="p-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Line Items</h3>
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 border-b text-xs text-gray-500"><th className="text-left px-3 py-2">Description</th><th className="text-right px-3 py-2 w-16">Qty</th><th className="text-right px-3 py-2 w-24">Price</th><th className="text-right px-3 py-2 w-24">Total</th></tr></thead>
                  <tbody>{cpo.line_items.map((it, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="px-3 py-2">{it.description}</td>
                      <td className="px-3 py-2 text-right">{it.quantity}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(it.unit_price)}</td>
                      <td className="px-3 py-2 text-right font-medium">{formatCurrency((it.quantity || 0) * (it.unit_price || 0))}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Quote Comparison */}
          {matchedQuote && (
            <Card className="rounded-sm border-blue-200 bg-blue-50/30">
              <CardContent className="p-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-600 mb-3">Matched Quote: {matchedQuote.quote_number}</h3>
                <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                  <div><p className="text-xs text-gray-500">Quote Total</p><p className="font-medium">{formatCurrency(matchedQuote.total)}</p></div>
                  <div><p className="text-xs text-gray-500">PO Total</p><p className="font-medium">{formatCurrency(cpo.total_amount)}</p></div>
                  <div><p className="text-xs text-gray-500">Difference</p><p className={`font-medium ${Math.abs(matchedQuote.total - cpo.total_amount) < 0.01 ? "text-green-600" : "text-amber-600"}`}>{formatCurrency(Math.abs(matchedQuote.total - cpo.total_amount))}</p></div>
                </div>
                <p className="text-xs text-gray-500">Match: <span className="font-medium capitalize">{matchedQuote.match_type}</span> — Confidence: <span className={`font-semibold ${matchedQuote.confidence === "high" ? "text-green-600" : matchedQuote.confidence === "medium" ? "text-amber-600" : "text-gray-500"}`}>{matchedQuote.confidence}</span></p>
                {matchedQuote.line_items?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <p className="text-xs text-gray-500 mb-2">Quote Line Items:</p>
                    <table className="w-full text-xs">
                      <tbody>{matchedQuote.line_items.map((it, i) => (
                        <tr key={i} className="border-b border-blue-100 last:border-0">
                          <td className="py-1 pr-2">{it.description}</td>
                          <td className="py-1 text-right w-12">{it.quantity}</td>
                          <td className="py-1 text-right w-20">{formatCurrency(it.unit_price)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Sidebar Info */}
        <div className="space-y-5">
          {/* OCR Info */}
          {cpo.ocr_data?.raw_text && (
            <Card className="rounded-sm border-gray-200">
              <CardContent className="p-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">OCR Extract</h3>
                <p className="text-xs text-gray-500 mb-1">Confidence: {(cpo.ocr_data.confidence * 100 || 0).toFixed(0)}%</p>
                <div className="bg-gray-50 rounded-sm p-3 max-h-48 overflow-y-auto">
                  <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono">{cpo.ocr_data.raw_text.substring(0, 800)}</pre>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quote Matches */}
          {cpo.quote_matches?.length > 0 && (
            <Card className="rounded-sm border-gray-200">
              <CardContent className="p-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Quote Matches ({cpo.quote_matches.length})</h3>
                <div className="space-y-2">
                  {cpo.quote_matches.map((q, i) => (
                    <div key={i} className={`p-2 rounded-sm border text-sm ${q.quote_id === cpo.matched_quote_id ? "border-blue-300 bg-blue-50" : "border-gray-200"}`}>
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-blue-600">{q.quote_number}</span>
                        <span className={`text-xs font-medium ${q.confidence === "high" ? "text-green-600" : q.confidence === "medium" ? "text-amber-600" : "text-gray-400"}`}>{q.confidence}</span>
                      </div>
                      <p className="text-xs text-gray-500">{formatCurrency(q.total)} — {q.match_type}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Approval Info */}
          {cpo.approval?.approved_at && (
            <Card className="rounded-sm border-green-200 bg-green-50/30">
              <CardContent className="p-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-green-600 mb-2">Approved</h3>
                <p className="text-xs text-gray-600">{formatDateLong(cpo.approval.approved_at)}</p>
              </CardContent>
            </Card>
          )}
          {cpo.approval?.rejected_at && (
            <Card className="rounded-sm border-red-200 bg-red-50/30">
              <CardContent className="p-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-red-600 mb-2">Rejected</h3>
                <p className="text-xs text-gray-600">{formatDateLong(cpo.approval.rejected_at)}</p>
                {cpo.approval.rejection_reason && <p className="text-sm text-red-700 mt-1">{cpo.approval.rejection_reason}</p>}
              </CardContent>
            </Card>
          )}

          {/* File Info */}
          {cpo.file_info?.filename && (
            <Card className="rounded-sm border-gray-200">
              <CardContent className="p-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Uploaded File</h3>
                <p className="text-sm font-medium">{cpo.file_info.filename}</p>
                <p className="text-xs text-gray-400">{(cpo.file_info.size / 1024).toFixed(1)} KB</p>
              </CardContent>
            </Card>
          )}

          {/* Email Info */}
          {cpo.source === "email" && (
            <Card className="rounded-sm border-gray-200">
              <CardContent className="p-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Email Import</h3>
                <p className="text-sm">{cpo.email_subject}</p>
                <p className="text-xs text-gray-400 mt-1">From: {cpo.email_from_name || cpo.email_from}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Match Quote Dialog */}
      <MatchQuoteDialog open={matchOpen} onOpenChange={setMatchOpen} cpo={cpo} onSuccess={fetch} />

      {/* Reject Dialog */}
      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent data-testid="cpo-reject-dialog">
          <AlertDialogHeader><AlertDialogTitle>Reject Customer PO</AlertDialogTitle>
            <AlertDialogDescription>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection..." className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none mt-2" data-testid="cpo-reject-reason" />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={reject} className="bg-red-600 hover:bg-red-700" data-testid="cpo-reject-confirm">Reject</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ======================== MATCH QUOTE DIALOG ========================

function MatchQuoteDialog({ open, onOpenChange, cpo, onSuccess }) {
  const [quotes, setQuotes] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [matching, setMatching] = useState(false);

  useEffect(() => {
    if (open && cpo?.customer_id) {
      setLoading(true);
      api.get("/quotes", { params: { customer_id: cpo.customer_id, limit: 20, status: "Sent" } })
        .then(r => setQuotes(r.data.quotes || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, cpo]);

  const match = async (quoteId) => {
    setMatching(true);
    try {
      await api.post(`/customer-pos/${cpo.cpo_id}/match-quote`, { quote_id: quoteId });
      toast.success("Quote matched"); onOpenChange(false); onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setMatching(false); }
  };

  const filtered = search ? quotes.filter(q => q.quote_number?.toLowerCase().includes(search.toLowerCase()) || q.reference?.toLowerCase().includes(search.toLowerCase())) : quotes;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="cpo-match-dialog">
        <DialogHeader><DialogTitle>Match to Quote</DialogTitle></DialogHeader>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search quotes..." className="rounded-sm mb-3" />
        {loading ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin text-gray-400" /></div> : filtered.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-400">No matching quotes found for this customer</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {filtered.map(q => (
              <div key={q.quote_id} className="flex items-center justify-between p-3 rounded-sm border hover:bg-gray-50">
                <div>
                  <p className="text-sm font-medium text-blue-600">{q.quote_number}</p>
                  <p className="text-xs text-gray-500">{formatCurrency(q.total)} — {q.status}</p>
                </div>
                <Button size="sm" onClick={() => match(q.quote_id)} disabled={matching} className="rounded-sm text-xs bg-[#E63946] hover:bg-[#C1121F] text-white" data-testid={`match-quote-${q.quote_id}`}>Match</Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ======================== SETTINGS ========================

function CPOSettingsDialog({ open, onOpenChange }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) api.get("/customer-pos/settings").then(r => setSettings(r.data)).catch(() => {});
  }, [open]);

  const save = async () => {
    setSaving(true);
    try { await api.put("/customer-pos/settings", settings); toast.success("Settings saved"); onOpenChange(false); }
    catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (!settings) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cpo-settings-dialog">
        <DialogHeader><DialogTitle>CPO Settings</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Auto-approve exact quote matches</label>
            <input type="checkbox" checked={settings.auto_approve_exact_match} onChange={e => setSettings({ ...settings, auto_approve_exact_match: e.target.checked })} className="w-4 h-4 accent-[#E63946]" data-testid="cpo-setting-auto-approve" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Matching Tolerance (%)</label>
            <Input type="number" min="0" max="20" value={settings.matching_tolerance_percent} onChange={e => setSettings({ ...settings, matching_tolerance_percent: parseFloat(e.target.value) || 0 })} className="rounded-sm" data-testid="cpo-setting-tolerance" />
            <p className="text-xs text-gray-400 mt-1">How much PO total can differ from quote total and still match</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Require Approval Above (ZAR)</label>
            <Input type="number" min="0" value={settings.require_approval_above} onChange={e => setSettings({ ...settings, require_approval_above: parseFloat(e.target.value) || 0 })} className="rounded-sm" data-testid="cpo-setting-approval-threshold" />
            <p className="text-xs text-gray-400 mt-1">0 = always require approval</p>
          </div>
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Email PO Detection</label>
            <input type="checkbox" checked={settings.email_detection_enabled} onChange={e => setSettings({ ...settings, email_detection_enabled: e.target.checked })} className="w-4 h-4 accent-[#E63946]" data-testid="cpo-setting-email-detect" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm" data-testid="cpo-settings-save">{saving ? "Saving..." : "Save Settings"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ======================== MAIN ========================

export default function CustomerPurchaseOrdersPage() {
  const { id } = useParams();
  return id ? <CPODetail cpoId={id} /> : <CPOList />;
}
